import { useCallback, useEffect, useState } from 'react';
import type { ValidationDefinition } from '../types';
import {
  filterSelectableValidationIds,
  getSelectableValidationDefinitionIds,
} from '../lib/validationReportNormalizer';
import { normalizeValidationId } from '../validationConstants';

export function useValidationConfigSelection(validations: ValidationDefinition[]): {
  selectedValidations: number[];
  setSelectedValidations: React.Dispatch<React.SetStateAction<number[]>>;
  handleToggleValidation: (id: number) => void;
  handleSelectAll: () => void;
  handleDeselectAll: () => void;
} {
  const [selectedValidations, setSelectedValidations] = useState<number[]>(
    getSelectableValidationDefinitionIds(validations)
  );

  useEffect(() => {
    setSelectedValidations((current) => filterSelectableValidationIds(current, validations));
  }, [validations]);

  const handleToggleValidation = useCallback((id: number) => {
    const normalizedId = normalizeValidationId(id);
    if (!filterSelectableValidationIds([normalizedId], validations).includes(normalizedId)) {
      return;
    }

    setSelectedValidations((prev) =>
      prev.includes(normalizedId)
        ? prev.filter((vid) => vid !== normalizedId)
        : filterSelectableValidationIds([...prev, normalizedId], validations)
    );
  }, [validations]);

  const handleSelectAll = useCallback(() => {
    setSelectedValidations(getSelectableValidationDefinitionIds(validations));
  }, [validations]);

  const handleDeselectAll = useCallback(() => {
    setSelectedValidations([]);
  }, []);

  return {
    selectedValidations,
    setSelectedValidations,
    handleToggleValidation,
    handleSelectAll,
    handleDeselectAll,
  };
}
